/* ============================================================
   Deal-flow module state.
   - Investor profile: the read-only mirror of the firm's Diligentsia
     investor settings, fetched once from GET /deal/profile and shared
     across the app bar, pipeline, dashboard, profile, triage, decision
     and decline flows (it is needed almost everywhere, so it lives here
     rather than being refetched per screen).
   - Session flags (decided / analysisDone) drive the stepper's
     all-ticked state and the analysis progress-vs-report switch. They
     are seeded from the backend on load and updated after mutations.
   ============================================================ */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchProfile } from './api';

const DealStateContext = createContext(null);

export function DealStateProvider({ children }) {
	const [decided, setDecided] = useState(() => new Set());
	const [analysisDone, setAnalysisDone] = useState(() => new Set());

	const [profile, setProfile] = useState(null);
	const [profileLoading, setProfileLoading] = useState(true);
	const [profileError, setProfileError] = useState(false);

	const loadProfile = useCallback(async () => {
		setProfileLoading(true); setProfileError(false);
		try {
			const p = await fetchProfile();
			setProfile(p || null);
			if (!p) setProfileError(true);
		} catch (e) {
			setProfileError(true);
		} finally {
			setProfileLoading(false);
		}
	}, []);

	useEffect(() => { loadProfile(); }, [loadProfile]);

	const markDecided = useCallback(id => setDecided(s => new Set(s).add(String(id))), []);
	const markAnalysisDone = useCallback(id => setAnalysisDone(s => new Set(s).add(String(id))), []);

	const value = {
		profile, profileLoading, profileError, reloadProfile: loadProfile,
		isDecided: id => decided.has(String(id)),
		isAnalysisDone: id => analysisDone.has(String(id)),
		markDecided,
		markAnalysisDone,
	};
	return <DealStateContext.Provider value={value}>{children}</DealStateContext.Provider>;
}

export function useDealState() {
	return useContext(DealStateContext);
}

// Convenience hook: the shared investor profile + its loading/error state.
export function useProfile() {
	const { profile, profileLoading, profileError, reloadProfile } = useDealState();
	return { profile, loading: profileLoading, error: profileError, reload: reloadProfile };
}
